import { useCallback, useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { useBootstrapWS } from "./useBootstrapWS";
import { toast } from "sonner";

/** Load and mutate Thermax boiler operator workspace. */
export function useBoilerWorkspace() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/boiler/workspace/status");
      setData(res.data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useBootstrapWS(["boiler_logging", "production_monitoring"], reload);

  const run = async (fn, successMsg) => {
    setSubmitting(true);
    try {
      const result = await fn();
      if (successMsg) toast.success(successMsg);
      await reload();
      return result?.data ?? true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const startBoiler = (fuelBeds) =>
    run(() => api.post("/boiler/start", { fuel_beds: fuelBeds }), "Boiler started");

  const takeOverShift = () =>
    run(() => api.post("/boiler/take-over"), "You are now the boiler operator");

  const endShift = (payload) =>
    run(() => api.post("/boiler/shift-end", payload), "Shift ended — boiler still running");

  const stopBoilerRun = (payload) =>
    run(() => api.post("/boiler/stop", payload), "Boiler stopped");

  const startBlowdown = () =>
    run(() => api.post("/boiler/blowdown/start"), "Blowdown started");

  const stopBlowdown = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/boiler/blowdown/stop");
      const seconds = res.data?.blowdown_log?.duration_seconds;
      toast.success(
        seconds != null ? `Blowdown stopped — ${formatDurationLabel(seconds)}` : "Blowdown stopped",
      );
      await reload();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const saveHourlyLog = (payload) =>
    run(() => api.post("/boiler/hourly-log", payload), "Reading saved");

  const cycleWaterLevel = () =>
    run(() => api.post("/boiler/water-level/cycle"), "Water level updated");

  const toggleHuskFeedBed = (bed) =>
    run(() => api.post("/boiler/husk-feed-bed/toggle", { bed }), `Bed ${bed} feed updated`);

  const saveWaterTest = (payload) =>
    run(() => api.post("/boiler/water-test", payload), "Daily water test saved");

  const reportBreakdown = (payload) =>
    run(() => api.post("/boiler/report-breakdown", payload), "Breakdown reported");

  return {
    data,
    loading,
    submitting,
    reload,
    startBoiler,
    takeOverShift,
    endShift,
    stopBoiler: stopBoilerRun,
    startBlowdown,
    stopBlowdown,
    saveHourlyLog,
    cycleWaterLevel,
    toggleHuskFeedBed,
    saveWaterTest,
    reportBreakdown,
    runtime: data?.runtime || {},
    operatorShift: data?.operator_shift || {},
    readings: data?.current_readings || {},
    hourlyCheck: data?.hourly_check || {},
    todayWaterTest: data?.today_water_test,
    waterTestDoneToday: data?.water_test_done_today,
    closureReasons: data?.closure_reasons || [],
    breakdownCategories: data?.breakdown_categories || [],
    pressureLimits: data?.steam_pressure_limits || { warn_low: 10, warn_high: 16.5 },
    lastBlowdownHoursAgo: data?.last_blowdown_hours_ago,
    blowdown: data?.blowdown || {},
    bedTracking: data?.bed_tracking || {},
  };
}

function formatDurationLabel(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}
